import pptxgen from 'pptxgenjs';

import { ProjectRunAnalysisQuestion, ProjectSalesforceInfo } from '../../Types/dealGPT';
import { formatBenchmarkOutput, getBenchmarkCategoryGroups } from '../../util/benchmarkUtils';

export interface BenchmarkSlideData {
  label: string;
  value: number;
  benchmarks: string[];
}

export const parseToBenchmarkSlideData = async (
  benchmarkQuestions: ProjectRunAnalysisQuestion[],
  salesforce: ProjectSalesforceInfo | null | undefined
): Promise<BenchmarkSlideData[]> => {
  const groupedBenchmarks = getBenchmarkCategoryGroups(benchmarkQuestions);

  return groupedBenchmarks
    .map((group) => {
      // The first question in each group is used to determine the overall percentage
      const firstQuestion = group.benchmarkQuestions[0];
      const selectedOption = firstQuestion.benchmark?.options.find(
        (o) => o.type === firstQuestion.value
      );
      const firstQuestionPercentage = selectedOption?.total
        ? Math.round((selectedOption.value * 100) / selectedOption.total)
        : 0;

      return {
        label: group.category,
        value: firstQuestionPercentage,
        benchmarks: group.benchmarkQuestions
          .map((q) => {
            const options = q.benchmark?.options || [];
            const selectedOption = options.find((o) => o.type === q.value);
            return selectedOption
              ? formatBenchmarkOutput(q.benchmark, selectedOption, salesforce)
              : null;
          })
          .filter((benchmarkOutput): benchmarkOutput is string => !!benchmarkOutput),
      };
    })
    .filter((category) => category.benchmarks.length > 0);
};

export const generateBenchmarkSlide = (
  pres: pptxgen,
  scope: string,
  benchmarkDate: Date,
  data: BenchmarkSlideData[],
  slideNumber = 2
) => {
  const chartSlide = pres.addSlide({ masterName: 'MASTER_SLIDE' });

  chartSlide.addText(`CORE ${scope.toUpperCase()} CAPABILITIES`, {
    x: 0.1,
    y: 0.2,
    w: '90%',
    h: 0.3,
    fontSize: 8,
    color: '50658E',
    fontFace: 'Arial',
    bold: true,
  });

  chartSlide.addText(`Core ${scope} capabilities metric definitions`, {
    x: 0.1,
    y: 0.47,
    w: '90%',
    h: 0.3,
    fontSize: 16,
    color: '070154',
    fontFace: 'Arial',
    bold: true,
  });

  chartSlide.addShape('rect', {
    x: 0.2,
    y: 0.92,
    w: 9.6,
    h: 0.3,
    fill: { color: '070154' },
    line: { color: '070154' },
  });

  chartSlide.addText(
    `Data was captured from West Monroe's Intellio® Insights platform on: ${benchmarkDate.toLocaleDateString()}`,
    {
      x: 0.2,
      y: 0.92,
      w: 9.6,
      h: 0.3,
      fontSize: 10,
      color: 'FFFFFF',
      bold: true,
      fontFace: 'Arial',
      align: 'center',
      valign: 'middle',
    }
  );

  const leftColumnX = 0.3;
  const rightColumnX = 5.1;
  const donutSize = 0.65;
  const circleTextGap = 0.1;
  const itemVerticalSpacing = 0.08;
  const textFontSize = 7;
  const percentFontSize = 7;
  const descriptionLineHeight = 0.14;
  const fullWidth = 9.6;
  const donutColor = 'D72D79';
  const incompleteColor = 'E7EEF8';
  const borderColor = '070154';

  const midPoint = Math.ceil(data.length / 2);
  const leftColumnItems = data.slice(0, midPoint);
  const rightColumnItems = data.slice(midPoint);

  const addCapabilityItem = (item: BenchmarkSlideData, x: number, y: number, isLast = false) => {
    const rowHeight = 0.43;
    const donutYOffset = (rowHeight - donutSize) / 2;

    chartSlide.addChart(
      'doughnut',
      [
        {
          name: item.label,
          labels: ['Complete', 'Incomplete'],
          values: [item.value, 100 - item.value],
        },
      ],
      {
        x,
        y: y + donutYOffset,
        w: donutSize,
        h: donutSize,
        showLabel: false,
        showLegend: false,
        chartColors: [donutColor, incompleteColor],
        dataLabelFontSize: 0,
        holeSize: 75,
        dataBorder: { pt: 1, color: borderColor },
      }
    );

    chartSlide.addText(`${item.value}%`, {
      x,
      y: y + donutYOffset + donutSize / 2 - 0.14,
      w: donutSize,
      h: 0.3,
      fontSize: percentFontSize,
      color: '070154',
      fontFace: 'Arial',
      bold: true,
      align: 'center',
      valign: 'middle',
    });

    let currentTextY =
      y + (rowHeight - (0.2 + item.benchmarks.length * descriptionLineHeight)) / 2 - 0.03;

    chartSlide.addText(item.label, {
      x: x + donutSize + circleTextGap,
      y: currentTextY,
      w: 4,
      h: 0.2,
      fontSize: textFontSize,
      color: '05013F',
      fontFace: 'Arial',
      bold: true,
      valign: 'top',
    });

    currentTextY += 0.2;
    item.benchmarks.forEach((line) => {
      chartSlide.addText(line, {
        x: x + donutSize + circleTextGap,
        y: currentTextY,
        w: 4,
        h: descriptionLineHeight,
        fontSize: textFontSize,
        color: '05013F',
        fontFace: 'Arial',
        valign: 'middle',
      });
      currentTextY += descriptionLineHeight;
    });

    const itemHeight = rowHeight;

    if (!isLast) {
      chartSlide.addShape('line', {
        x: 0.2,
        y: y + itemHeight + 0.03,
        w: fullWidth,
        h: 0,
        line: { color: 'E6E6E6', width: 1 },
      });
    }

    return itemHeight;
  };

  let currentY = 1.35;
  leftColumnItems.forEach((item, idx) => {
    const isLast = idx === leftColumnItems.length - 1;
    const h = addCapabilityItem(item, leftColumnX, currentY, isLast);
    currentY += h + itemVerticalSpacing;
  });

  currentY = 1.35;
  rightColumnItems.forEach((item, idx) => {
    const isLast = idx === rightColumnItems.length - 1;
    const h = addCapabilityItem(item, rightColumnX, currentY, isLast);
    currentY += h + itemVerticalSpacing;
  });

  chartSlide.addText(
    `Benchmarks derive from West Monroe's Intellio Insights™ platform that sources data from ${scope} Advisory for Private Equity (CAPE), due diligence, and other West Monroe ${scope} engagements. The benchmarks represent the percentage of organizations with the minimally recommended capability`,
    {
      x: 5.2,
      y: 4.45,
      w: 4.5,
      h: 0.5,
      fontSize: 6,
      color: '070154',
      fontFace: 'Arial',
      italic: true,
    }
  );

  chartSlide.addText(slideNumber.toString(), {
    x: 9.0,
    y: 4.85,
    w: 1.0,
    h: 0.8,
    align: 'right',
    valign: 'middle',
    fontSize: 7.5,
    color: '484481',
    fontFace: 'Arial',
    bold: false,
  });
};
